/**
 * Curated SEO search (Blade mirror of Vue Vehicle/Index): widgets rendered like
 * Marketplace.vue, VehicleHits.vue and Algolia/Pagination.vue instead of the canonical
 * /marketplace ones. Used by marketplace-search.ts when `config.curatedLayout` is set.
 */
import { connectClearRefinements, connectCurrentRefinements } from "instantsearch.js/es/connectors";
import type { PaginationRenderState } from "instantsearch.js/es/connectors/pagination/connectPagination";
import { escapeHtml } from "./widgets/helpers";
import { holdScrollPosition } from "@/shared/scroll-restore";

/** True on the curated SEO search page (window.__MARKETPLACE_CONFIG__.curatedLayout). */
export function isCuratedLayoutPage(): boolean {
    return typeof window !== "undefined" && Boolean(window.__MARKETPLACE_CONFIG__?.curatedLayout);
}

/** Empty facet label: Menu.vue (vue-i18n `noResults`) on the curated page. */
export function facetNoResultsLabel(): string {
    return isCuratedLayoutPage() ? "Pas de résultats" : "Aucun résultat";
}

/**
 * Menu.vue renders `<CurrentRefinement :is-into-facet>` between its search box and its
 * list: an empty ais-CurrentRefinements (a 1rem spacer) or the refined values. Curated
 * page only: the mount is filled by createCuratedFacetWidgets().
 */
export function curatedFacetRefinementsSpacer(attribute: string): string {
    return isCuratedLayoutPage()
        ? `<div class="ais-CurrentRefinements ais-CurrentRefinements--noRefinement" data-curated-facet-refinements="${attribute}"><div class="current-refinements facet"><div></div></div></div>`
        : "";
}

/** Marketplace.vue HITS_DESKTOP / HITS_MOBILE. */
export const CURATED_HITS_DESKTOP = 48;
export const CURATED_HITS_MOBILE = 24;

/** Marketplace.vue `isMobileDevice()`: iOS user agent or a viewport under 768px. */
export function isCuratedMobileDevice(): boolean {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
        return false;
    }
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || window.innerWidth < 768;
}

function pageFromUrl(): number {
    const pageParam = new URLSearchParams(window.location.search).get("page");
    return pageParam ? Math.max(0, parseInt(pageParam, 10) - 1) : 0;
}

/**
 * Marketplace.vue keeps 48 hits on the first mobile page (the SSR ones) but shows only 24
 * of them; as soon as the visitor changes page (or lands on ?page=N) it asks for 24.
 */
let hasNavigated = pageFromUrl() > 0;

export function curatedHitsPerPage(): number {
    if (isCuratedMobileDevice() && hasNavigated) {
        return CURATED_HITS_MOBILE;
    }
    return CURATED_HITS_DESKTOP;
}

/**
 * Number of hits shown, undefined = all of them. On a mobile device Marketplace.vue shows
 * 24 hits: 24 of the 48 SSR ones on the first page (displayLimit), then pages of 24. The
 * Blade SSR grid (48 hits) can be kept by the reconciliation, so it is capped here too.
 */
export function curatedDisplayLimit(): number | undefined {
    return isCuratedMobileDevice() ? CURATED_HITS_MOBILE : undefined;
}

/** Marketplace.vue handlePaginationPageChange. */
export function onCuratedPageChange(_page: number): void {
    hasNavigated = true;
    applyCuratedDisplayLimit();
}

/** Hides the hits past the display limit (SSR and client grids). */
export function applyCuratedDisplayLimit(): void {
    const limit = curatedDisplayLimit();
    document
        .querySelectorAll<HTMLElement>("#hits .marketplace-hits__list > li")
        .forEach((li, index) => {
            li.hidden = limit !== undefined && index >= limit;
        });
}

/** VehicleHits.vue « no results » panel. */
export function curatedNoResultsTemplate(): string {
    return `<div class="no-results-container"><div class="no-results-content"><h2 class="no-results-title"> 0 véhicules pour votre recherche </h2><p class="no-results-text"> Plus simple peut être mieux. Réessayez avec moins de critères de recherche ou avec des critères différents. </p></div></div>`;
}

/** Algolia/Pagination.vue getDisplayedPages. */
const MILESTONE_PAGES = [10, 20, 30, 50, 100, 1000, 10000];

function getDisplayedPages(current: number, total: number): (number | "...")[] {
    const pageSet = new Set<number>();
    for (let i = 1; i <= Math.min(3, total); i++) pageSet.add(i);
    if (current > 1) pageSet.add(current - 1);
    pageSet.add(current);
    if (current < total) pageSet.add(current + 1);
    for (const m of MILESTONE_PAGES) {
        if (m <= total) pageSet.add(m);
    }
    if (total > 0) pageSet.add(total);

    const sorted = Array.from(pageSet).sort((a, b) => a - b);
    const pages: (number | "...")[] = [];
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) pages.push("...");
        pages.push(sorted[i]);
    }
    return pages;
}

/**
 * Renders Algolia/Pagination.vue inside `#pagination` (vue-instantsearch root
 * `.ais-Pagination`, `--noRefinement` on a single page). VehicleHits.vue renders it only
 * when there are hits.
 */
export function renderCuratedPagination(container: HTMLElement, renderState: PaginationRenderState): void {
    const { currentRefinement, nbPages, nbHits, isFirstPage, isLastPage, refine, createURL } = renderState;

    if (!nbHits) {
        container.innerHTML = "";
        return;
    }

    const current = currentRefinement + 1;
    const link = (target: number, inner: string, disabled = false): string =>
        `<a data-attr="listing-page-link" class="ais-Pagination-link${disabled ? " disabled" : ""}" href="${escapeHtml(createURL(target))}" data-page="${target}">${inner}</a>`;

    const items = getDisplayedPages(current, nbPages)
        .map((page) => {
            if (page === "...") {
                return `<li class="ais-Pagination-ellipsis"> ... </li>`;
            }
            return `<li>${link(page - 1, `<span class="ais-Pagination-item${page === current ? " selected" : ""}">${page}</span>`)}</li>`;
        })
        .join("");

    const root = nbPages <= 1 ? "ais-Pagination ais-Pagination--noRefinement" : "ais-Pagination";
    container.innerHTML = `<div class="${root}"><ul class="ais-Pagination-list"><li>${link(
        currentRefinement - 1,
        `<span class="ais-Pagination-item previousPage${isFirstPage ? " disabled" : ""}">‹</span>`,
        isFirstPage,
    )}</li>${items}<li>${link(
        currentRefinement + 1,
        `<span class="ais-Pagination-item nextPage${isLastPage ? " disabled" : ""}">›</span>`,
        isLastPage,
    )}</li></ul></div>`;

    container.querySelectorAll<HTMLAnchorElement>("a[data-page]").forEach((a) => {
        a.addEventListener("click", (e) => {
            e.preventDefault();
            if (a.classList.contains("disabled")) return;
            const page = parseInt(a.dataset.page ?? "0", 10);
            if (Number.isNaN(page)) return;
            refine(page);
            window.scrollTo({ top: 0, behavior: "smooth" });
            onCuratedPageChange(page);
        });
    });
}

/**
 * Marketplace.vue `headerHitsReverseColor`: while the footer is on screen (75px top
 * margin), HitsHeader's save-search button and mobile filter button turn yellow.
 */
export function initCuratedReverseColor(): void {
    const footer = document.querySelector(".footer");
    if (!footer || typeof IntersectionObserver === "undefined") return;

    const targets = () =>
        document.querySelectorAll<HTMLElement>(
            ".hits-action .hits-action__save-search-button, .hits-action .hits-action__sort--mobile",
        );

    new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                targets().forEach((el) => el.classList.toggle("reverse-color", entry.isIntersecting));
            }
        },
        { rootMargin: "-75px 0px 0px 0px" },
    ).observe(footer);
}

/** Close.vue icon of CurrentRefinement.vue (same as widgets/range-filters-pills.ts). */
const CLOSE_ICON =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="current-refinements__item__close"><path d="M3.81202 3L3 3.81202L7.18798 8L3 12.188L3.81202 13L8 8.81202L12.188 13L13 12.188L8.81202 8L13 3.81202L12.188 3L8 7.18798L3.81202 3Z" fill="currentColor"></path></svg>';

/** vue-i18n `energyType.*`. */
const ENERGY_TYPE_LABELS: Record<string, string> = {
    diesel: "Diesel",
    electric: "Électrique",
    essence: "Essence",
    hybrid: "Hybride",
};

/**
 * CurrentRefinement.vue (hero, « Filtres Algolia existants »): one link per refinement,
 * labels translated like the Vue page (body type / gearbox labels come from the page
 * props, empty on the curated page).
 */
export function createCuratedCurrentRefinementsWidget(options: {
    container: string;
    bodyTypeLabels: Record<string, string>;
    gearboxTypeLabels: Record<string, string>;
}) {
    const label = (attribute: string, value: string): string => {
        switch (attribute) {
            case "energy_type":
                return ENERGY_TYPE_LABELS[value] ?? value;
            case "seats":
                return `${value} places`;
            case "body_type":
                return options.bodyTypeLabels[value] || value;
            case "gearbox":
                return options.gearboxTypeLabels[value] || value;
            default:
                return value;
        }
    };

    return connectCurrentRefinements((renderState) => {
        const container = document.querySelector<HTMLElement>(options.container);
        if (!container) return;

        const refinements = renderState.items.flatMap((item) =>
            item.refinements.map((refinement) => ({ item, refinement })),
        );

        container.innerHTML = refinements
            .map(({ refinement }, index) => {
                const text =
                    refinement.type === "numeric"
                        ? `${refinement.operator === "<=" ? "≤" : refinement.operator === ">=" ? "≥" : ""} ${Number(
                              refinement.value,
                          ).toLocaleString("fr-FR")} `
                        : label(refinement.attribute, String(refinement.value));
                return `<a data-attr="listing-active-filter-remove" class="current-refinements__item" href="${escapeHtml(
                    renderState.createURL(refinement),
                )}" data-refinement-index="${index}"><span>${escapeHtml(text)}</span>${CLOSE_ICON}</a>`;
            })
            .join("");

        container.querySelectorAll<HTMLAnchorElement>("a[data-refinement-index]").forEach((a) => {
            a.addEventListener("click", (event) => {
                event.preventDefault();
                const entry = refinements[Number(a.dataset.refinementIndex)];
                entry?.item.refine(entry.refinement);
            });
        });
    })({});
}

/** ClearRefinement.vue attributes (the `.clear-filters-facet` mounts of the sidebar). */
const FACET_CLEAR_ATTRIBUTES = [
    "model",
    "specification",
    "options",
    "reference_exterior_color",
    "reference_interior_color",
    "reference_interior_material",
];

/**
 * Curated page widgets of the sidebar: the global reset (Marketplace.vue
 * ais-clear-refinements), one ClearRefinement.vue per facet, and the in-facet
 * CurrentRefinement.vue of Menu.vue (make, model, options).
 */
export function createCuratedFacetWidgets(): unknown[] {
    const resetLink = (url: string, marker = "listing-filters-clear") =>
        `<a class="clear-filters-button" href="${escapeHtml(url)}" data-attr="${marker}">Réinitialiser</a>`;

    const globalClear = connectClearRefinements((renderState) => {
        const container = document.getElementById("clear-refinements");
        if (!container) return;
        // Vue falls back to the stock (hidden) disabled button when it cannot refine.
        container.innerHTML = `<div class="ais-ClearRefinements">${
            renderState.canRefine
                ? resetLink(renderState.createURL())
                : '<button type="reset" class="ais-ClearRefinements-button ais-ClearRefinements-button--disabled" disabled=""> Clear refinements </button>'
        }</div>`;
        container.querySelector("a")?.addEventListener("click", (event) => {
            event.preventDefault();
            renderState.refine();
        });
    })({});

    const facetClears = FACET_CLEAR_ATTRIBUTES.map((attribute) =>
        connectClearRefinements((renderState) => {
            document
                .querySelectorAll<HTMLElement>(`[data-curated-facet-clear="${attribute}"]`)
                .forEach((mount) => {
                    mount.innerHTML = renderState.canRefine
                        ? resetLink(renderState.createURL(), "listing-panel-clear-facet")
                        : "";
                    mount.querySelector("a")?.addEventListener("click", (event) => {
                        event.preventDefault();
                        renderState.refine();
                    });
                });
        })({ includedAttributes: [attribute] }),
    );

    const inFacetRefinements = connectCurrentRefinements((renderState) => {
        document.querySelectorAll<HTMLElement>("[data-curated-facet-refinements]").forEach((mount) => {
            const attribute = mount.dataset.curatedFacetRefinements ?? "";
            const entries = renderState.items
                .filter((item) => item.attribute === attribute)
                .flatMap((item) => item.refinements.map((refinement) => ({ item, refinement })));
            mount.classList.toggle("ais-CurrentRefinements--noRefinement", entries.length === 0);
            mount.innerHTML = `<div class="current-refinements facet"><div>${entries
                .map(
                    ({ refinement }, index) =>
                        `<label class="RefinementList" data-attr="listing-panel-clear-facet"><span><span class="RefinementList__labelText">${escapeHtml(
                            String(refinement.label),
                        )}</span></span><input class="RefinementList__clear" type="checkbox" checked value="${escapeHtml(
                            String(refinement.value),
                        )}" data-refinement-index="${index}"></label>`,
                )
                .join("")}</div></div>`;
            mount.querySelectorAll<HTMLInputElement>("input[data-refinement-index]").forEach((input) => {
                input.addEventListener("input", (event) => {
                    event.preventDefault();
                    const entry = entries[Number(input.dataset.refinementIndex)];
                    entry?.item.refine(entry.refinement);
                });
            });
        });
    })({});

    return [globalClear, ...facetClears, inFacetRefinements];
}

/**
 * Marketplace.vue `<Teleport to="#header-action-bar-mobile" :disabled="!actionBarMobile">`:
 * below 960px the action bar lives in the header, above it stays in the page (hidden),
 * right before #marketplace-content. The Blade header renders it server-side.
 */
export function initCuratedActionBarTeleport(): void {
    const target = document.getElementById("header-action-bar-mobile");
    const bar = target?.querySelector<HTMLElement>(".marketplace-action-bar");
    const content = document.getElementById("marketplace-content");
    if (!target || !bar || !content) return;

    const mql = window.matchMedia("(max-width: 959px)");
    const place = () => {
        if (mql.matches) {
            if (bar.parentElement !== target) target.appendChild(bar);
        } else if (bar.nextElementSibling !== content) {
            content.before(bar);
        }
    };
    place();
    mql.addEventListener("change", place);
}

/**
 * MarketplaceHeader.vue `<Teleport to="#header-searchbox-mobile" :disabled="!isMobile">`
 * around Autocomplete.vue: the search box follows a (max-width: 959px) listener, from the
 * hero to the header and back, keeping its state and its hero options (Vue mounts it in
 * the hero before the teleport turns on). `mountedInHeader` is where marketplace-search.ts
 * mounted it at load.
 */
export function initCuratedSearchTeleport(mountedInHeader: boolean): void {
    const hero = document.getElementById("searchbox");
    const header = document.getElementById("header-searchbox-mobile");
    if (!hero || !header) return;

    const mql = window.matchMedia("(max-width: 959px)");
    let current = mountedInHeader ? header : hero;
    mql.addEventListener("change", () => {
        const target = mql.matches ? header : hero;
        if (target === current) return;
        // The header keeps a static search shell for the pages without a search widget.
        target.replaceChildren(...Array.from(current.childNodes));
        current = target;
    });
}

/** Inertia's history state key (router.saveDocumentScrollPosition). */
const SCROLL_STATE_KEY = "documentScrollPosition";

function navigationType(): string | undefined {
    const entry = performance.getEntriesByType?.("navigation")[0] as PerformanceNavigationTiming | undefined;
    return entry?.type;
}

function savedScrollTop(): number | null {
    const state = window.history.state as Record<string, unknown> | null;
    const position = state?.[SCROLL_STATE_KEY] as { top?: unknown } | undefined;
    const top = Number(position?.top);
    return Number.isFinite(top) ? top : null;
}

function saveScrollPosition(): void {
    try {
        const state = (window.history.state as Record<string, unknown> | null) ?? {};
        window.history.replaceState(
            { ...state, [SCROLL_STATE_KEY]: { top: Math.round(window.scrollY), left: Math.round(window.scrollX) } },
            "",
        );
    } catch {
        // History state unavailable (sandboxed frame): the back then starts at the top.
    }
}

/**
 * Inertia (Vue page) sets the history scroll restoration to manual and keeps the window
 * scroll in the history state of the entry (saved on scroll, 100 ms debounce only: a link
 * followed within 100 ms of a scroll keeps the previous position). On a back,
 * a forward or a reload it puts the visitor back at that position, whichever link they
 * left through (vehicle card, breadcrumb, footer, another site), even after a full reload.
 * The Blade page does the same, holding the position while its layout settles (card
 * images, hydrated widgets) so the browser's scroll anchoring does not move it.
 */
export function initCuratedScrollRestoration(search: { once(event: "render", listener: () => void): unknown }): void {
    if (!("scrollRestoration" in window.history)) return;
    window.history.scrollRestoration = "manual";

    const type = navigationType();
    const target = type === "back_forward" || type === "reload" ? savedScrollTop() : null;

    if (target !== null && target > 0) {
        const hold = holdScrollPosition(target);
        search.once("render", () => requestAnimationFrame(hold.apply));
    }

    let timer: number | undefined;
    window.addEventListener(
        "scroll",
        () => {
            window.clearTimeout(timer);
            timer = window.setTimeout(saveScrollPosition, 100);
        },
        { passive: true },
    );
}
